/**
 * hpq-flyout-auth-state.js — reconcile flyout/drawer UI with CAA auth state.
 *
 * Shopify Customer Account API (CAA / Headless) stores tokens at the
 * 'hpq_caa_tokens' localStorage key, but does NOT set the storefront
 * secure_customer_sig / customer_sf_token cookies. Liquid's
 * {% if customer %} returns false for CAA-only-authed users, so drawer-menu
 * items rendered server-side default to the logged-out variant.
 *
 * This script toggles classes on <html>:
 *   - `hpq-caa-authed`              when CAA tokens are present in localStorage
 *   - `hpq-storefront-logged-in`    when HPQ_CONFIG.customer.storefront_logged_in
 *                                   is true (server-injected by theme-globals)
 *
 * CSS in custom.css uses either class to swap the Account label + reveal the
 * Logout row. See task #33.
 */
(function () {
  'use strict';

  var TOKENS_KEY = 'hpq_caa_tokens';
  var CAA_AUTHED_CLASS = 'hpq-caa-authed';
  var STOREFRONT_AUTHED_CLASS = 'hpq-storefront-logged-in';

  // Session validity keys on the ACCESS token + its absolute expiresAt — never
  // on the id_token. Shopify's refresh grant returns NO id_token (verified
  // against production 2026-08-19), so after the first silent refresh the
  // stored blob permanently reads { idToken: null, accessToken: ..., expiresAt: ... }.
  // The old check (t && t.idToken && t.accessToken) therefore read a fully
  // signed-in shopper as logged out ~1s after every page load: the
  // hpq-caa-authed class came off, the drawer showed REGISTER/LOGIN, the
  // /account click launched a redundant popup login, and the logout intercept
  // fell through to Shopify's confirmation page.
  function hasCaaTokens() {
    try {
      // Prefer the CAA module's own session-validity helper so this file can't
      // drift from hpq-customer-account-api.js's definition of "signed in".
      var api = window.HPQCustomerAccountAPI;
      if (api && typeof api.hasUsableSession === 'function') {
        return api.hasUsableSession();
      }
      // Load-order fallback: this script is deferred with the API module, but
      // they attach from different snippets — on pages where only this one is
      // present, inline the same rule (access token + unexpired expiresAt).
      // Read EXPIRY_SKEW_MS from the API module if it is attached (even
      // without a hasUsableSession export), and fall back to a literal
      // ONLY when the module is genuinely absent — the literal is the
      // single point of truth across the codebase now.
      var skew = (api && typeof api.EXPIRY_SKEW_MS === 'number')
        ? api.EXPIRY_SKEW_MS
        : 30000;
      var raw = window.localStorage.getItem(TOKENS_KEY);
      if (!raw) return false;
      var t = JSON.parse(raw);
      if (!t || !t.accessToken) return false;
      if (typeof t.expiresAt !== 'number') return false;
      return Date.now() < t.expiresAt - skew;
    } catch (e) {
      return false;
    }
  }

  function reconcile() {
    var root = document.documentElement;
    if (hasCaaTokens()) root.classList.add(CAA_AUTHED_CLASS);
    else root.classList.remove(CAA_AUTHED_CLASS);

    var cfg = window.HPQ_CONFIG && window.HPQ_CONFIG.customer;
    if (cfg && cfg.storefront_logged_in === true) {
      root.classList.add(STOREFRONT_AUTHED_CLASS);
    } else {
      root.classList.remove(STOREFRONT_AUTHED_CLASS);
    }
  }

  reconcile();

  window.addEventListener('storage', function (e) {
    if (e.key === TOKENS_KEY) reconcile();
  });

  // Same-tab token writes (PKCE callback, silent auth bridge, clearTokens
  // during logout) don't fire `storage` events. hpq-customer-account-api.js
  // dispatches this CustomEvent so we can update the drawer immediately.
  document.addEventListener('hpq:auth-state-changed', reconcile);

  window.HPQ_RECONCILE_AUTH_STATE = reconcile;

  // Logged-out user clicks any /account link → run PKCE flow instead of
  // Shopify's hosted login URL. Shopify-canonical pattern per
  // shopify.dev/docs/api/customer/latest "Authorization request": full-page
  // window.location.href to the authorization_endpoint with PKCE params
  // lands the user back on the storefront with tokens in localStorage.
  // routes.account_url alone would route to shopify.com customer dashboard
  // and never bridge tokens to the storefront — breaks every CAA-dependent
  // feature (fortune /recognize, drawer auth reconciliation, etc.). Covers
  // both the drawer "REGISTER / LOGIN" and the desktop header account icon.
  document.addEventListener('click', function (e) {
    var el = e.target && typeof e.target.closest === 'function'
      ? e.target.closest('a[href*="/account"]')
      : null;
    if (!el) return;
    var href = el.getAttribute('href') || '';
    if (/\/logout/.test(href)) return;
    if (window.HPQ_DEBUG) {
      try {
        console.log('[HPQ-FLYOUT] /account click captured', {
          href: href.slice(0, 80),
          defaultPrevented: e.defaultPrevented,
          storefront_logged_in: !!(window.HPQ_CONFIG && window.HPQ_CONFIG.customer && window.HPQ_CONFIG.customer.storefront_logged_in),
          hasTokens: hasCaaTokens(),
          caaAvail: !!(window.HPQCustomerAccountAPI && window.HPQCustomerAccountAPI.startPkceFlow),
          clientId: !!window.HPQ_CAA_CLIENT_ID
        });
      } catch (_) {}
    }
    if (hasCaaTokens()) return;
    var cfg = window.HPQ_CONFIG && window.HPQ_CONFIG.customer;
    if (cfg && cfg.storefront_logged_in === true) return;
    var caa = window.HPQCustomerAccountAPI;
    var clientId = window.HPQ_CAA_CLIENT_ID;
    if (!caa || !caa.startPkceFlow || !clientId) {
      try { console.warn('[HPQ-FLYOUT] /account click: cannot start PKCE — falling back to anchor href', { caa: !!caa, startPkceFlow: !!(caa && caa.startPkceFlow), clientId: !!clientId }); } catch (_) {}
      return;
    }
    // Capture-phase + early stopPropagation to win over any drawer-menu /
    // theme.js handlers that might call location.href manually.
    e.preventDefault();
    e.stopImmediatePropagation();
    if (window.HPQ_DEBUG) {
      try { console.log('[HPQ-FLYOUT] /account click: starting PKCE flow'); } catch (_) {}
    }

    var shopDomain = window.HPQ_CAA_SHOP_DOMAIN || window.location.hostname;
    var scope = 'openid email customer-account-api:full';

    // Desktop: log in via a popup window so the shopper never leaves the page.
    // On success reconcile the drawer (Account/Logout) in place. Mobile,
    // popup-blocked, and older browsers fall back to the full-page redirect.
    if (caa.startPkcePopupFlow && caa.isPopupLoginSupported && caa.isPopupLoginSupported()) {
      caa.startPkcePopupFlow({
        shopDomain: shopDomain,
        clientId: clientId,
        scope: scope,
        redirectUri: caa.buildPopupRedirectUri()
      }).then(function (result) {
        if (result && result.ok) {
          if (typeof window.HPQ_RECONCILE_AUTH_STATE === 'function') {
            window.HPQ_RECONCILE_AUTH_STATE();
          }
          return;
        }
        if (result && result.reason === 'popup_blocked') {
          startAccountRedirectLogin(caa, shopDomain, clientId, scope);
        }
        // Closed / timed out / OAuth error: leave the drawer logged-out as-is.
      });
      return;
    }

    startAccountRedirectLogin(caa, shopDomain, clientId, scope);
  }, true);

  // Full-page redirect login — the mobile / popup-blocked / no-popup fallback.
  // Captures the pre-redirect path so the callback handler can restore it.
  function startAccountRedirectLogin(caa, shopDomain, clientId, scope) {
    // Skip capture when already on the callback URL — a click during callback
    // would store nonsense (search still has code+state params).
    try {
      var search = window.location.search || '';
      if (!/[?&]fortune_oauth_callback=1\b/.test(search)) {
        window.sessionStorage.setItem(
          'hpq:pkce-return-path',
          window.location.pathname + search
        );
      }
    } catch (e) {}
    caa.startPkceFlow({
      shopDomain: shopDomain,
      clientId: clientId,
      scope: scope,
      redirectUri: caa.buildCanonicalRedirectUri()
    });
  }

  // [data-hpq-logout] click intercept — single-click CAA logout via OIDC
  // end_session_endpoint when tokens are cached. Falls through to the
  // anchor's href (routes.account_logout_url, which shows a confirmation
  // page) when no tokens are present — covers the race window where the
  // user logs in via the flyout and clicks Logout before silent auth has
  // bridged tokens.
  document.addEventListener('click', function (e) {
    var el = e.target && typeof e.target.closest === 'function'
      ? e.target.closest('[data-hpq-logout]')
      : null;
    if (!el) return;
    if (!hasCaaTokens()) return;
    var caa = window.HPQCustomerAccountAPI;
    if (!caa || !caa.logout) return;
    e.preventDefault();
    caa.logout({
      shopDomain: window.HPQ_CAA_SHOP_DOMAIN || window.location.hostname,
      // Storefront origin must be allowlisted in Shopify Headless CAA →
      // Application setup → Logout URI(s). Without this, Shopify falls back
      // to its default Logout URI (shopify.com/account), which triggers an
      // auth_retry chain and lands the user on an error page.
      postLogoutRedirectUri: window.location.origin + '/'
    });
  });
})();
